import { MaxNumbersOfSuggestions } from 'constants/suggestion';
import { useMemo } from 'react';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { getAvailableSlots } from 'components/Filters/helpers/getAvailableSlots';
import getSuggestionsThatStartsWith from '../helpers/getSuggestionsThatStartsWith';
import { Suggestions } from '../types';
import { useSuggestedLabel } from './useSuggestedLabel';
import { useSuggestedPack } from './useSuggestedPack';

const useSuggestions = ({
    searchInput,
    hasActiveSearch,
    showOnlyFilterSuggestions,
    suggestionsFromFilters,
}: {
    searchInput?: string;
    hasActiveSearch: boolean;
    showOnlyFilterSuggestions: boolean;
    suggestionsFromFilters: Suggestions[];
}) => {
    const { getFilterLabel } = useApiFiltersContext();

    const suggestedLabels = useSuggestedLabel({
        showOnlyFilterSuggestions,
        searchInput,
        hasActiveSearch,
    });

    const suggestedPacks = useSuggestedPack({
        showOnlyFilterSuggestions,
        searchInput,
        hasActiveSearch,
    });

    const autocompleteSuggestions = useMemo(() => {
        if (!searchInput) {
            return undefined;
        }

        const filterSuggestions: Suggestions[] = getSuggestionsThatStartsWith(
            suggestionsFromFilters,
            searchInput,
            getFilterLabel,
        );

        const {
            availableFilterSlots,
            availableLabelSlots,
            availablePackSlots,
        } = getAvailableSlots({
            filtersLength: filterSuggestions.length,
            labelsLength: suggestedLabels.length,
            packsLength: suggestedPacks.length,
        });

        const suggestionsSliced = filterSuggestions.slice(
            0,
            availableFilterSlots,
        );

        const labelSliced = suggestedLabels.slice(0, availableLabelSlots);
        const packSliced = suggestedPacks.slice(0, availablePackSlots);

        const suggestionsToAdd = [...labelSliced, ...packSliced];

        suggestionsSliced.splice(
            suggestionsSliced.length,
            0,
            ...suggestionsToAdd,
        );

        return suggestionsSliced;
    }, [
        searchInput,
        suggestionsFromFilters,
        suggestedLabels,
        suggestedPacks,
        getFilterLabel,
    ]);

    const suggestions = useMemo(
        () =>
            autocompleteSuggestions ??
            suggestionsFromFilters.slice(0, MaxNumbersOfSuggestions),
        [autocompleteSuggestions, suggestionsFromFilters],
    );

    return {
        suggestions,
    };
};

export default useSuggestions;
